import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, FileDown, RefreshCw, ExternalLink, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as FilingService from '@/services/filingService';
import * as CIPCService from '@/services/cipcService';

interface StatutoryFilingsProps {
  companyId: string;
  companyName: string;
}

export function StatutoryFilings({ companyId, companyName }: StatutoryFilingsProps) {
  const [periodStart, setPeriodStart] = useState<string>('');
  const [periodEnd, setPeriodEnd] = useState<string>('');
  const [filings, setFilings] = useState<FilingService.FilingJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingCoR39, setGeneratingCoR39] = useState(false);
  const [generatingCoR15_2, setGeneratingCoR15_2] = useState(false);

  async function loadFilings() {
    try {
      setLoading(true);
      const data = await FilingService.getFilingJobs(companyId);
      setFilings(data);
    } catch (err: any) {
      console.error('Error loading filings:', err);
      toast.error('Failed to load filing jobs');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateFiling() {
    try {
      setGenerating(true);

      // Create the filing job
      const job = await FilingService.createFilingJob(
        companyId,
        'FULL_STAT_PACK',
        periodStart || null,
        periodEnd || null,
        null
      );

      // Generate the filing pack using edge function
      const result = await FilingService.generateFilingPack(
        companyId,
        'FULL_STAT_PACK',
        periodStart || null,
        periodEnd || null,
        job.id
      );

      toast.success('Filing pack generated successfully');

      // Open the manifest in a new tab
      window.open(result.manifestUrl, '_blank');

      // Reset form
      setPeriodStart('');
      setPeriodEnd('');

      // Reload filings
      await loadFilings();
    } catch (err: any) {
      console.error('Error generating filing:', err);
      toast.error('Failed to generate filing pack');
    } finally {
      setGenerating(false);
    }
  }

  async function handleViewFiling(job: FilingService.FilingJob) {
    try {
      if (job.output_zip_url) {
        window.open(job.output_zip_url, '_blank');
      } else {
        toast.error('No filing pack available for this job');
      }
    } catch (err: any) {
      console.error('Error opening filing:', err);
      toast.error('Failed to open filing pack');
    }
  }

  async function handleGenerateCoR39() {
    try {
      setGeneratingCoR39(true);
      const result = await FilingService.generateCoR39(companyId);

      if (result.success) {
        if (result.url) {
          await recordGenerated('CoR39', result.url);
        }
        toast.success(
          'CoR39 generated. File it at CIPC e-Services, then record the reference here.'
        );
      } else {
        throw new Error(result.error || 'Failed to generate CoR39');
      }
    } catch (err: any) {
      console.error('Error generating CoR39:', err);
      toast.error(err.message || 'Failed to generate CoR39');
    } finally {
      setGeneratingCoR39(false);
    }
  }

  async function handleGenerateCoR15_2() {
    try {
      setGeneratingCoR15_2(true);
      const result = await FilingService.generateCoR15_2(companyId);

      if (result.success) {
        if (result.url) {
          await recordGenerated('CoR15.2', result.url);
        }
        toast.success(
          'CoR15.2 generated. File it at CIPC e-Services, then record the reference here.'
        );
      } else {
        throw new Error(result.error || 'Failed to generate CoR15.2');
      }
    } catch (err: any) {
      console.error('Error generating CoR15.2:', err);
      toast.error(err.message || 'Failed to generate CoR15.2');
    } finally {
      setGeneratingCoR15_2(false);
    }
  }

  /**
   * Records that a filing document exists and is waiting to be lodged.
   *
   * Nothing is sent to CIPC. The previous version posted the document to an
   * unverified API and marked the job ACKNOWLEDGED on any 200, while swallowing
   * failures so a filing could appear lodged when it was not.
   */
  async function recordGenerated(filingForm: string, filingUrl: string) {
    try {
      await CIPCService.recordGeneratedFiling(companyId, filingForm, filingUrl);
      await loadFilings();
    } catch (err: any) {
      console.error('Error recording generated filing:', err);
      // The document generated fine; only the bookkeeping failed, and saying so
      // is better than the old silence.
      toast.warning('Filing generated, but it could not be added to the filing record.');
    }
  }

  useEffect(() => {
    if (companyId) {
      loadFilings();
    }
  }, [companyId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-500';
      case 'FAILED':
        return 'bg-red-500';
      case 'RUNNING':
        return 'bg-blue-500';
      case 'PENDING':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Statutory Filings Console</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateCoR39}
            disabled={generatingCoR39}
          >
            {generatingCoR39 ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            CoR39
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateCoR15_2}
            disabled={generatingCoR15_2}
          >
            {generatingCoR15_2 ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            CoR15.2
          </Button>
          <Button variant="outline" size="sm" onClick={loadFilings} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Generate Filing Form */}
        <div className="p-4 border rounded-lg bg-muted/30">
          <h3 className="text-sm font-medium mb-4">Generate Statutory Pack</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="period-start" className="text-xs">
                Period Start (Optional)
              </Label>
              <Input
                id="period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="period-end" className="text-xs">
                Period End (Optional)
              </Label>
              <Input
                id="period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleGenerateFiling} disabled={generating} className="w-full">
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4 mr-2" />
                    Generate Pack
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Leave dates empty to generate a pack for all time. Pack includes: Shareholder Register,
            Allotment Register, Transfer Register, Board Minutes, Certificates Index, and Valuation
            Snapshot.
          </p>
        </div>

        {/* Filing History */}
        <div>
          <h3 className="text-sm font-medium mb-3">Filing History</h3>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filings.length > 0 ? (
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Output</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filings.map((filing) => (
                    <TableRow key={filing.id}>
                      <TableCell className="font-medium">
                        {filing.filing_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {filing.period_start && filing.period_end
                          ? `${new Date(filing.period_start).toLocaleDateString()} - ${new Date(
                              filing.period_end
                            ).toLocaleDateString()}`
                          : 'All Time'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(filing.status)}>{filing.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(filing.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {filing.output_zip_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewFiling(filing)}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Open
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center p-8 border rounded-lg">
              <FileDown className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-sm text-muted-foreground">
                No filing packs generated yet. Use the form above to create your first statutory
                filing pack.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
